import { useAuth } from '@agentnet/auth';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { saveFile } from 'api/file/file-api';
import { getCountiesPropertyFromList } from 'api/file/file-property-api';
import { ICreateFileResponse } from 'api/file/interfaces/create-file';
import { CountyType, fileInfoType, propertyType } from 'api/file/interfaces/get-file';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import useAsync from 'hooks/useAsync';
import { Dispatch, FC, SetStateAction, useContext, useEffect, useState } from 'react';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import AgentNetDropdownSelector, { SelectOption } from 'ui-kit/inputs/AgentNetDropdownSelector';
import { JacketCountySchema } from 'utilities/validation/schemas/jacket-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import { propertyCountyRequired } from './constants';

interface CountySelectorProps {
  executeGetJackets: () => void;
  setIsCountySelected: Dispatch<SetStateAction<boolean>>;
}

const CountySelector: FC<CountySelectorProps> = ({ executeGetJackets, setIsCountySelected }) => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      countyText: {
        marginBottom: theme.spacing(2),
      },
    }),
  );
  const classes = useStyles();

  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { fileData, loadFileData } = fileDataCtx;

  const [validationErrors, setValidationErrors] = useState<FieldValidationError[]>([]);
  const [shouldShowValidation, setShouldShowValidation] = useState(false);

  const [countyOptions, setCountyOptions] = useState<SelectOption[]>([]);
  const [fileInfo, setFileInfo] = useState<fileInfoType | null | undefined>(fileData);

  const [displayError, setDisplayError] = useState<boolean>(false);
  const [errMsgs, setErrMsgs] = useState([]);

  const { addGlobalMsg } = useGlobalMessages();
  const { getAccessToken } = useAuth();

  const getCountiesPropertyFromListData = async (): Promise<CountyType> => {
    const token = await getAccessToken();
    const response = await getCountiesPropertyFromList(
      fileData?.properties?.[0].state ?? '',
      fileData?.accountId ?? 0,
      token,
    );
    return response;
  };

  const {
    execute: propertyListExecute,
    value: propertyListResults,
    status: propertyListStatus,
    errors: propertyListErrors,
  } = useAsync<CountyType>(getCountiesPropertyFromListData, false);

  useEffect(() => {
    if (propertyListResults) {
      const countyList: SelectOption[] = [];
      propertyListResults?.counties?.forEach((county) => {
        const option = {
          name: county,
          value: county,
        };
        countyList.push(option);
      });
      setCountyOptions(countyList);
    }
  }, [propertyListResults]);

  useEffect(() => {
    propertyListExecute();
  }, []);

  const saveFileData = async (): Promise<ICreateFileResponse> => {
    const token = await getAccessToken();
    return await saveFile(fileInfo, fileInfo?.fileId ?? '', token);
  };

  const {
    execute: executeSaveFile,
    value: saveFileValue,
    status: saveFileStatus,
    errors: saveFileErrors,
  } = useAsync<ICreateFileResponse>(saveFileData, false);

  useEffect(() => {
    if (saveFileValue) {
      loadFileData && loadFileData({ fileId: saveFileValue.fileId });
      setIsCountySelected(true);
      executeGetJackets && executeGetJackets();
    }
  }, [saveFileValue]);

  useEffect(() => {
    setDisplayError(saveFileStatus === 'error' || propertyListStatus === 'error');
  }, [saveFileStatus, propertyListStatus]);

  const getErrorMessages = () => {
    if (saveFileErrors?.length) {
      return saveFileErrors;
    }
    if (propertyListErrors?.length) {
      return propertyListErrors;
    }
    return [];
  };

  useEffect(() => {
    const errorMessages = getErrorMessages();
    setErrMsgs(errorMessages);
  }, [displayError]);

  useEffect(() => {
    if (displayError && errMsgs.length) {
      errMsgs?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [displayError, errMsgs]);

  useEffect(() => {
    validateErrors(fileInfo?.properties?.[0] ?? {});
  }, [fileInfo?.properties?.[0]]);

  const validateErrors = async (property: propertyType) => {
    const errs: FieldValidationError[] = (await doValidate(property, JacketCountySchema)) || [];
    setValidationErrors(errs);
  };

  const updateCounty = (county: string) => {
    setFileInfo((prevFileData) => ({
      ...prevFileData,
      properties: [
        {
          ...prevFileData?.properties?.[0],
          county: county,
        },
      ],
    }));
  };

  const savePropertyCounty = async () => {
    setShouldShowValidation(true);
    if (validationErrors.length === 0) {
      executeSaveFile().then();
    }
  };

  return (
    <AgentNetConfirmationDialog
      onConfirm={savePropertyCounty}
      open={true}
      size="xs"
      dialogTitle="Property County Selection Required"
      dialogBtnContent="Save"
      disabled={saveFileStatus === 'pending'}
      showSecondaryActionBtn={false}
      showCloseIcon={false}
      dialogActionBtnJustifyContent="flex-end"
      dialogTextHTML={
        <>
          <Typography variant="body2" className={classes.countyText}>
            {propertyCountyRequired}
          </Typography>
          <AgentNetDropdownSelector
            label="County"
            name="county"
            qaAttribute="JacketCounty"
            options={countyOptions}
            value={fileInfo?.properties?.[0]?.county || ''}
            menuOption={(value: string) => {
              updateCounty(value);
            }}
            dropdowntype="outlined"
            required
            errs={validationErrors}
            showValidation={shouldShowValidation}
          />
        </>
      }
    />
  );
};

export default CountySelector;
